import React, { useState } from 'react';
import { Box, Alert, Snackbar } from '@mui/material';
import { User, CreateUserRequest, UpdateUserRequest } from '../types';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { UserList, UserDialog, DeleteUserDialog } from '../features/admin';
import { errorMessage } from '../utils/errorMessage';
import { useUserMutations } from '../hooks/useUserMutations';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const { saveUserMutation, deleteUserMutation } = useUserMutations();
  const isLoading = saveUserMutation.isPending || deleteUserMutation.isPending;

  const normalizeEmail = (email?: string) => (email || '').trim().toLowerCase();
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmitUser = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      const emailChanged = Boolean(
        selectedUser
        && data.email
        && normalizeEmail(data.email) !== normalizeEmail(selectedUser.email),
      );

      if (emailChanged && selectedUser) {
        const { email, ...profileData } = data as UpdateUserRequest;
        await apiClient.requestEmailChange(selectedUser.id, { newEmail: email! });
        await saveUserMutation.mutateAsync({
          id: selectedUser.id,
          data: profileData,
          isSuperAdmin: currentUser?.role === 'SUPER_ADMIN',
        });
        showSnackbar(t('users.emailVerificationSent'), 'info');
        setIsDialogOpen(false);
        return;
      }

      await saveUserMutation.mutateAsync({
        id: selectedUser?.id,
        data,
        isSuperAdmin: currentUser?.role === 'SUPER_ADMIN',
      });
      showSnackbar(selectedUser ? t('users.updateSuccess') : t('users.createSuccess'));
      
      setIsDialogOpen(false);
    } catch (error: unknown) {
      showSnackbar(errorMessage(error, t('users.saveError')), 'error');
      throw error;
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUserMutation.mutateAsync(userToDelete.id);
      showSnackbar(t('users.deleteSuccess'));
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error: unknown) {
      showSnackbar(errorMessage(error, t('users.deleteError')), 'error');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  return (
    <Box>
      <UserList
        onCreateUser={handleCreateUser}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
      />

      <UserDialog
        open={isDialogOpen}
        user={selectedUser}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitUser}
        isLoading={isLoading}
      />

      <DeleteUserDialog
        open={isDeleteDialogOpen}
        user={userToDelete}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        isLoading={isLoading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
